import { CheckCircle, CheckSquare, Square } from "@tamagui/lucide-icons";
import { action, computed, makeObservable, observable } from "mobx";
import { ModuleType } from "../../../templates/types";
import type { Api } from "../../core/apis";
import type { Stores } from "../../core/stores";
import type { PlanTag, PlanTagWithOptions } from "../../plans/PlanModel";
import BaseModuleModel, {
  type MenuOption,
  type ModuleModelData,
} from "../core/BaseModuleModel";
import { getDefaultSettingsForModule } from "../utils";
import { CheckboxModuleBlockUI, CheckboxModuleUI } from "./CheckboxModuleUI";
import CheckboxModel from "./data/CheckboxModel";

export type CheckboxModuleSnapshot = {
  checked: number;
  unchecked: number;
};

export type CheckboxModuleSettings = {
  sortCheckedAtBottom: boolean;
  useTags: boolean;
};

export interface CheckboxModuleModelData
  extends ModuleModelData<CheckboxModuleSnapshot> {
  settings: CheckboxModuleSettings;
}

export default class CheckboxModuleModel extends BaseModuleModel<
  CheckboxModuleSnapshot,
  CheckboxModuleModelData
> {
  constructor(stores: Stores, api: Api, data: CheckboxModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      settings: getDefaultSettingsForModule(
        ModuleType.Checkboxes,
        data.settings,
      ),
    });

    makeObservable(this);
  }

  // Used to show setting
  hasHelperGuide = true;

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = this.checkedStats;
  }

  @computed get checkedStats() {
    let checked = 0;
    let unchecked = 0;

    this.checkboxes.forEach((d) => {
      if (d.data.checked) {
        checked++;
      } else {
        unchecked++;
      }
    });

    return { checked, unchecked };
  }

  @action async updateSettings(
    settings: CheckboxModuleSettings,
    skipSave?: boolean,
  ) {
    this.data.settings = settings;

    if (!skipSave) {
      await this.api.modulesApi.set(this);
    }
  }

  // Used for new items if set
  @observable currentTag?: PlanTag;
  @observable showTagModal = false;

  filterItemsBasedNonActiveOnTags(items: CheckboxModel[]) {
    if (!this.nonActiveTags.length) {
      return items;
    }

    return items.filter((item) => {
      if (item.data.tags.length === 0) {
        return this.nonActiveTags.length === 0;
      }

      if (
        item.data.tags.every(
          (tagUid) => !!this.nonActiveTags.find((t) => t.uid === tagUid),
        )
      ) {
        return false;
      }

      return true;
    });
  }

  @computed get sortedItems(): CheckboxModel[] {
    const sortedItems: CheckboxModel[] = this.checkboxes;

    if (this.data.settings.sortCheckedAtBottom) {
      sortedItems.sort((a, b) => {
        if (a instanceof CheckboxModel && b instanceof CheckboxModel) {
          return a.data.checked === b.data.checked
            ? 0
            : a.data.checked
              ? 1
              : -1;
        }

        if (a instanceof CheckboxModel) {
          return a.data.checked ? 1 : 0;
        }

        if (b instanceof CheckboxModel) {
          return b.data.checked ? -1 : 0;
        }

        return 0;
      });
    }

    return this.filterItemsBasedNonActiveOnTags(sortedItems).slice();
  }

  @observable nonActiveTags: PlanTag[] = [];
  @action toggleActiveTag(tag: PlanTag) {
    if (this.nonActiveTags.length === 0) {
      // Disable the rest
      this.nonActiveTags = this.planTags.filter((t) => t.uid !== tag.uid);
      return;
    }

    const foundTag = this.nonActiveTags.find((t) => t.uid === tag.uid);
    if (foundTag) {
      this.nonActiveTags = this.nonActiveTags.filter((t) => t.uid !== tag.uid);
    } else {
      this.nonActiveTags.push(tag);
    }

    if (this.nonActiveTags.length === this.planTags.length) {
      this.nonActiveTags.length = 0; // reset to view all
    }

    this.sendTelemetryEvent("tag_active_toggled");
  }

  @computed get checkboxes(): CheckboxModel[] {
    return this.dataItems.filter(
      (item) => item instanceof CheckboxModel,
    ) as CheckboxModel[];
  }

  @computed get planTags(): PlanTagWithOptions[] {
    const tagCache = new Map<string, { count: number; completed: number }>();

    this.checkboxes.forEach((item) => {
      if (item.data.tags.length) {
        item.data.tags.forEach((tagUid) => {
          const foundTag = tagCache.get(tagUid);
          if (!foundTag) {
            tagCache.set(tagUid, {
              count: 1,
              completed: item.data.checked ? 1 : 0,
            });
          } else {
            tagCache.set(tagUid, {
              count: foundTag.count + 1,
              completed: foundTag.completed + (item.data.checked ? 1 : 0),
            });
          }
        });
      }
    });

    const tags: PlanTagWithOptions[] = [];
    Array.from(tagCache.entries()).forEach(([uid, options]) => {
      const planTag = this.plan.getTag(uid);
      if (planTag) {
        tags.push({
          ...planTag,
          count: options.count,
          completed: options.completed,
          dimmed: !!this.nonActiveTags.find((t) => t.uid === uid),
        });
      }
    });

    return tags.sort((a, b) => a.label.localeCompare(b.label));
  }

  toFireStore(): Omit<CheckboxModuleModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      settings: this.data.settings,
    };
  }

  render() {
    return <CheckboxModuleUI module={this} />;
  }

  renderStatic() {
    return null;
  }

  renderBlock() {
    return <CheckboxModuleBlockUI module={this} />;
  }

  renderStaticBlock() {
    return this.renderBlock();
  }

  renderIcon(size?: number) {
    return <CheckCircle size={size} />;
  }

  renderMenuOptions(): MenuOption[] {
    return [
      {
        icon: this.data.settings.sortCheckedAtBottom ? CheckSquare : Square,
        label: "Sort completed at the bottom",
        onPress: () => {
          this.updateSettings({
            ...this.data.settings,
            sortCheckedAtBottom: !this.data.settings.sortCheckedAtBottom,
          });
        },
      },
      {
        icon: this.data.settings.useTags ? CheckSquare : Square,
        label: "Use tags",
        onPress: () => {
          this.updateSettings({
            ...this.data.settings,
            useTags: !this.data.settings.useTags,
          });
        },
      },
    ];
  }
}
